import React from 'react';
import FormError from './FormError';
import HelpTooltip from './HelpTooltip';

interface FormFieldProps {
  /** Label text for the field */
  label: string;
  /** The id of the input element (for label htmlFor) */
  htmlFor: string;
  /** Whether the field is required */
  required?: boolean;
  /** Error message to display */
  error?: string;
  /** Helper text to display below the input */
  helperText?: string;
  /** Help text shown on label hover */
  labelHelpText?: string;
  /** The input element(s) to render */
  children: React.ReactNode;
  /** Optional className for the form group container */
  className?: string;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  htmlFor,
  required = false,
  error,
  helperText,
  labelHelpText,
  children,
  className = '',
}) => {
  const errorId = error ? `${htmlFor}-error` : undefined;
  const helperId = helperText && !error ? `${htmlFor}-helper` : undefined;
  const describedBy = errorId || helperId;

  // Styles matching existing .form-group patterns from App.css
  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 500,
    color: 'var(--text-primary, #ffffff)',
  };

  const requiredStyle: React.CSSProperties = {
    color: 'var(--danger, #dc3545)',
    marginLeft: '0.25rem',
  };

  const helperStyle: React.CSSProperties = {
    display: 'block',
    marginTop: '0.5rem',
    fontSize: '0.875rem',
    color: 'var(--orange-light, #ffa64d)',
    fontStyle: 'italic',
  };

  const errorInputStyle: React.CSSProperties = {
    borderColor: 'var(--danger, #dc3545)',
  };

  // Clone children to add aria attributes and error styling
  const enhancedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      const additionalProps: Record<string, unknown> = {};

      // Add ARIA attributes for accessibility
      if (error) {
        additionalProps['aria-invalid'] = true;
      }
      if (describedBy) {
        additionalProps['aria-describedby'] = describedBy;
      }

      // Add error border styling if there's an error
      if (error && child.props) {
        const existingStyle = (child.props as { style?: React.CSSProperties }).style || {};
        additionalProps.style = { ...existingStyle, ...errorInputStyle };
      }

      return React.cloneElement(child, additionalProps);
    }
    return child;
  });

  return (
    <div className={`form-group ${className}`.trim()} style={{ marginBottom: '1rem' }}>
      <label htmlFor={htmlFor} style={labelStyle}>
        <HelpTooltip helpText={labelHelpText}>
          {label}{required && <span style={requiredStyle} aria-hidden="true">*</span>}
        </HelpTooltip>
      </label>
      {enhancedChildren}
      {error && <FormError id={errorId} message={error} />}
      {helperText && !error && (
        <span id={helperId} style={helperStyle}>
          {helperText}
        </span>
      )}
    </div>
  );
};

export default FormField;
